import { useEffect, useMemo, useState } from 'react'

import {
  ColumnAdapter,
  ConditionBooleanOperator,
  ConditionEqualityOperator,
  ConditionFeatureFlag,
  ConditionStatementColumn,
  ConditionStatementFormData,
  ConditionTextOperator,
  ConditionValue,
  DefaultConditionOperator,
} from '@makyoapp/makyo-common'
import { SelectDropdown } from '@makyoapp/makyo-ui'

import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { ComponentSettingKey } from '@/shared/builder'

import { getOperatorOptions } from '../../shared/condition'

interface ConditionOperatorSelectV2Props {
  id?: string
  selected: DefaultConditionOperator
  value: ConditionValue
  onChange: (operator: DefaultConditionOperator) => void
  index: number
}

export function ConditionOperatorSelectV2({ id, value, onChange, selected, index }: ConditionOperatorSelectV2Props) {
  const { tables } = useDbClientContext()
  const { activeLayerClient } = useActiveLayerContext()

  const [colObj, setColObj] = useState<ColumnAdapter | undefined>(undefined)
  const cond = value.conditions[index]

  const { tbl, col } = (cond.lhs as ConditionStatementColumn) || {
    tbl: undefined,
    col: undefined,
  }

  const isProfile = cond.lhs.flag === ConditionFeatureFlag.ProfileData
  const isForm = cond.lhs.flag === ConditionFeatureFlag.FormData

  const selTable = useMemo(() => {
    if (tbl) {
      return tables.find((tb) => tb.id === tbl)
    }
    if (isProfile) {
      return tables.find((tb) => tb.id === window.appUserTableID)
    }
    if (isForm) {
      const formId = (cond.lhs as ConditionStatementFormData).formId
      const form = activeLayerClient?.componentsMap.get(formId as string)
      const formSettings = form?.settings
      const dataSource = formSettings?.[ComponentSettingKey.FormDataSource]
      return tables.find((t) => t.id === dataSource)
    }
  }, [activeLayerClient?.componentsMap, cond.lhs, isForm, isProfile, tables, tbl])

  useEffect(() => {
    if (selTable) {
      const selCol = selTable.columns.find((c) => c.id === col)
      if (selCol) {
        setColObj(selCol)
      }
    }
  }, [selTable, setColObj, col])

  return (
    <div className='w-full min-w-0'>
      <SelectDropdown
        id={id}
        value={selected}
        onChange={(opt) => {
          onChange(opt)
        }}
        toggleClassName='dark:hover:bg-slate-800!'
        menuContainerClassName='min-w-[200px]'
        options={
          colObj && selTable
            ? (Object.values(getOperatorOptions(colObj, selTable, tables)) as DefaultConditionOperator[])
            : value.conditions[index].lhs?.flag === ConditionFeatureFlag.UserLoggedIn
              ? Object.values(ConditionBooleanOperator)
              : value.conditions[index].lhs?.flag === ConditionFeatureFlag.Breakpoint
                ? Object.values(ConditionEqualityOperator)
                : Object.values(ConditionTextOperator)
        }
        renderOption={(opt) => {
          const split = opt.split('-').slice(1).join(' ')
          return split.charAt(0).toUpperCase() + split.slice(1)
        }}
      />
    </div>
  )
}
